import { useMemo } from 'react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  useIsMobile,
} from '@hotelise/ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format, addMonths, startOfMonth, isBefore, isEqual } from 'date-fns';
import type { MergedGuest } from './guestUtils';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface Props {
  guests: MergedGuest[];
  /** Selected date range — used to fill empty months on x-axis */
  dateRange?: DateRange;
  /** How many top countries to show individually; rest become "Other" */
  maxCountries?: number;
}

const FALLBACK_COLORS = [
  'hsl(210, 80%, 55%)', 'hsl(350, 80%, 60%)', 'hsl(45, 90%, 55%)',
  'hsl(160, 50%, 50%)', 'hsl(280, 60%, 60%)', 'hsl(30, 80%, 55%)',
  'hsl(190, 70%, 50%)', 'hsl(0, 60%, 50%)', 'hsl(120, 50%, 45%)',
  'hsl(330, 70%, 55%)',
];

export default function GuestArrivalsChart({ guests, dateRange, maxCountries = 7 }: Props) {
  const isMobile = useIsMobile();

  const { chartData, countries, countryColorMap } = useMemo(() => {
    // 1. Collect data per year-month
    const rawTotals: Record<string, number> = {};
    const monthCountryRaw: Record<string, Record<string, number>> = {};

    guests.forEach((g) => {
      const country = g.country || 'Unknown';
      g.reservations.forEach((r) => {
        const key = format(r.checkin, 'yyyy-MM');
        rawTotals[country] = (rawTotals[country] || 0) + 1;
        if (!monthCountryRaw[key]) monthCountryRaw[key] = {};
        monthCountryRaw[key][country] = (monthCountryRaw[key][country] || 0) + 1;
      });
    });

    // 2. Build full list of months from date range (fill gaps with zero)
    const allMonthKeys: string[] = [];
    if (dateRange?.from && dateRange?.to) {
      let cursor = startOfMonth(dateRange.from);
      const end = startOfMonth(dateRange.to);
      while (isBefore(cursor, end) || isEqual(cursor, end)) {
        allMonthKeys.push(format(cursor, 'yyyy-MM'));
        cursor = addMonths(cursor, 1);
      }
    } else {
      // Fallback: use only months present in data
      allMonthKeys.push(...Object.keys(monthCountryRaw).sort());
    }

    const sortedKeys = allMonthKeys.map((key) => [key] as [string]);

    // 3. Pick top N countries by total count; everything else → "Other"
    const ranked = Object.entries(rawTotals).sort((a, b) => b[1] - a[1]);
    const topCountries = ranked.slice(0, maxCountries).map(([name]) => name);
    const otherCountries = new Set(ranked.slice(maxCountries).map(([name]) => name));
    const hasOther = otherCountries.size > 0;

    const displayCountries = hasOther ? [...topCountries, 'Other'] : topCountries;

    // 4. Build per-month data with "Other" bucket
    const yearSet = new Set(allMonthKeys.map((key) => key.slice(0, 4)));
    const monthLabelFormat = yearSet.size > 1 ? 'MMM yy' : 'MMM';
    const builtChartData = sortedKeys.map(([key]) => {
      const raw = monthCountryRaw[key] || {};
      const label = format(new Date(`${key}-15`), monthLabelFormat);
      const entry: Record<string, any> = { month: label };
      let otherSum = 0;
      let total = 0;

      Object.entries(raw).forEach(([c, count]) => {
        if (otherCountries.has(c)) {
          otherSum += count;
        } else {
          entry[c] = count;
        }
        total += count;
      });

      // Ensure top countries have 0 even if absent this month
      topCountries.forEach((c) => { if (!(c in entry)) entry[c] = 0; });
      if (hasOther) entry.Other = otherSum;
      entry._total = total;
      return entry;
    });

    // 5. Assign colors
    const colorMap: Record<string, string> = {};
    displayCountries.forEach((c, i) => {
      colorMap[c] = c === 'Other' ? 'hsl(0, 0%, 55%)' : FALLBACK_COLORS[i % FALLBACK_COLORS.length];
    });

    return { chartData: builtChartData, countries: displayCountries, countryColorMap: colorMap };
  }, [guests, dateRange, maxCountries]);

  const chartConfig = useMemo(() => {
    const cfg: Record<string, { label: string; color: string }> = {};
    countries.forEach((c) => {
      cfg[c] = { label: c, color: countryColorMap[c] };
    });
    return cfg;
  }, [countries, countryColorMap]);

  const totalGuests = chartData.reduce((s, d) => s + ((d._total as number) || 0), 0);

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 border overflow-hidden min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg font-semibold">Guest Arrivals</h2>
          <p className="text-sm text-muted-foreground">{totalGuests} arrivals</p>
        </div>
      </div>

      {countries.length === 0 ? (
        <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
          No guest data for selected range
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className={`${isMobile ? 'h-[300px]' : 'h-[400px]'} w-full [&>div]:!aspect-auto`}
        >
          <BarChart
            data={chartData}
            margin={{
              top: isMobile ? 10 : 20,
              right: isMobile ? 10 : 30,
              left: isMobile ? 0 : 10,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12 }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 25 : 35} />
            <ChartTooltip content={<ChartTooltipContent hideLabel={false} />} />
            {countries.map((country) => (
              <Bar
                key={country}
                dataKey={country}
                stackId="countries"
                fill={countryColorMap[country]}
                radius={0}
              />
            ))}
          </BarChart>
        </ChartContainer>
      )}

      {countries.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4">
          {countries.map((country) => (
            <div key={country} className="flex items-center gap-1.5 text-sm">
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: countryColorMap[country] }}
              />
              <span className="text-muted-foreground">{country}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
